import dayjs from 'dayjs';

import type { Booking, BookingPayment, PrintLetterhead } from '../api/bookings';
import { paymentKindLabel, paymentMethodLabel } from '../bookings/paymentWords';
import { bookingReference, referenceLabel } from '../bookings/reference';
import { formatDocumentDate } from '../format/dates';
import { formatMoney } from '../format/money';
import { encodeQr, fits, type QrSymbol } from '../format/qr';

import {
  chargeBlock,
  depositBlock,
  moneyPhase,
  returnExposure,
  settledDeposit,
} from './customerMoney';

/**
 * The thermal receipt — what a 58 mm or 80 mm counter printer receives, line by line (spec §8's
 * receipt, M1 slice 6 of `docs/Mobile_Apps_Design.md`).
 *
 * <p>
 * <strong>The layout is the whole document, already padded to the paper's columns.</strong> A
 * thermal printer in text mode has a fixed number of characters a line — 32 on 58 mm paper, 48 on
 * 80 mm at the standard font — and nothing reflows. So the phone's preview draws these lines in a
 * monospace face and the printer's encoder sends the same lines; the two cannot disagree about where
 * a figure wraps, because neither of them decides.
 * </p>
 *
 * <p>
 * <strong>The money comes from the customer copy's blocks</strong> (`customerMoney.ts`), never added
 * up here, and keeps that document's three rules: the deposit is its own block and never inside the
 * invoice total; damage is never given a figure before inspection ("assessed at inspection"); and a
 * late-fee line is left off where no rate is set, because a printed zero is a promise.
 * </p>
 */

export type ReceiptPaper = '58mm' | '80mm';

/** Characters a line at the printer's standard font (Font A, 12×24). */
export const paperColumns: Record<ReceiptPaper, number> = { '58mm': 32, '80mm': 48 };

export interface ReceiptLine {
  /** Already padded to the paper's width (half of it on a double-size line). */
  text: string;
  bold?: boolean;
  /**
   * Double width and height — the shop's name and the figure received. A double line holds half
   * the columns, which is why the layout, not the printer, wraps it.
   */
  double?: boolean;
  /**
   * A QR symbol drawn in place of the text, centred and {@link qrShare} of the printable width
   * square. The text stays empty; the number it encodes is the line after it, for a person who
   * cannot scan.
   */
  qr?: QrSymbol;
}

/** How much of the roll's printable width a QR line takes — the page and the preview both. */
export const qrShare = 0.45;

export interface ReceiptInput {
  booking: Booking;
  letterhead: PrintLetterhead;
  /** The payment this receipt is for. Absent prints the booking's money as it stands. */
  payment?: BookingPayment | null;
  /**
   * Payments taken in the same press as {@link payment} — the deposit taken with the token at
   * booking — printed on the same paper: each receipt number, one figure received, and a line for
   * what each was for. Still two payments, so the deposit never reads as part of the rental.
   */
  together?: readonly BookingPayment[];
  paper: ReceiptPaper;
  /** An instant (ISO), printed at the foot so a reprint can be told from the original. */
  printedAt: string;
}

/* ------------------------------------------------------------ the characters */

/**
 * The few non-ASCII characters this product's own wording uses, turned into what any ESC/POS
 * printer prints. **The rupee sign is in no standard code page**, so it is `Rs.`, which is what
 * Indian counter receipts print anyway.
 *
 * Anything else outside ASCII — a customer's name in Gujarati — is left as it is. Every way this
 * document leaves the phone renders it as a page (PDF, picture, the print dialog, and the thermal
 * printer as an image), and the page engine shapes any script; rewriting a name would be worse.
 */
export function thermalText(text: string): string {
  return (
    text
      .replace(/₹\s?/g, 'Rs.')
      .replace(/[·•]/g, '-')
      .replace(/[−–—]/g, '-')
      .replace(/×/g, 'x')
      .replace(/[‘’]/g, "'")
      .replace(/[“”]/g, '"')
      .replace(/…/g, '...')
      // A no-break space, written by code point so no editor or shell can turn it back into one.
      .replaceAll(String.fromCharCode(0xa0), ' ')
  );
}

/* --------------------------------------------------------------- the columns */

/** Word-wraps to a width; a word longer than the line is broken rather than overflowing it. */
export function wrap(text: string, width: number): string[] {
  const lines: string[] = [];
  let current = '';

  for (const word of text.split(/\s+/).filter(Boolean)) {
    let rest = word;

    while (rest.length > width) {
      if (current) {
        lines.push(current);
        current = '';
      }

      lines.push(rest.slice(0, width));
      rest = rest.slice(width);
    }

    if (!current) {
      current = rest;
    } else if (current.length + 1 + rest.length <= width) {
      current += ` ${rest}`;
    } else {
      lines.push(current);
      current = rest;
    }
  }

  if (current) {
    lines.push(current);
  }

  return lines.length > 0 ? lines : [''];
}

export function centre(text: string, width: number): string {
  const gap = Math.max(0, width - text.length);

  return ' '.repeat(Math.floor(gap / 2)) + text;
}

/**
 * A label on the left and a figure on the right. A label too long to share the line wraps, and the
 * figure stays on the label's last line — so a column of figures stays a column.
 */
export function pair(label: string, value: string, width: number): string[] {
  const room = width - value.length - 1;

  if (room < 8) {
    // A value too wide to share a line with any useful label — a long name, usually: label above,
    // the value wrapped beneath it and right-aligned, so it still reads as the label's answer.
    return [...wrap(label, width), ...wrap(value, width).map((line) => line.padStart(width))];
  }

  const labelLines = wrap(label, room);
  const last = labelLines.pop() ?? '';

  return [...labelLines, last + value.padStart(width - last.length)];
}

/* -------------------------------------------------------------- the writer */

/**
 * The pieces every thermal document is written with — the receipt and the tax invoice alike, so two
 * documents on one roll cannot disagree about what a rule or a heading looks like. Every helper
 * turns this product's own punctuation into ASCII and wraps to the width, so a caller never can
 * overflow the paper by forgetting to.
 */
export function thermalWriter(width: number) {
  const lines: ReceiptLine[] = [];
  const half = Math.floor(width / 2);

  // `indent` is explicit because wrapping splits on whitespace, so leading spaces in the text
  // itself would be dropped — which is how the item details under each invoice line lost theirs.
  const plain = (text: string, bold = false, indent = 0) =>
    lines.push(
      ...wrap(thermalText(text), width - indent).map((t) => ({
        text: ' '.repeat(indent) + t,
        bold,
      })),
    );
  const centred = (text: string, bold = false) =>
    lines.push(...wrap(thermalText(text), width).map((t) => ({ text: centre(t, width), bold })));
  const row = (label: string, value: string, bold = false, indent = 0) =>
    lines.push(
      ...pair(thermalText(label), thermalText(value), width - indent).map((t) => ({
        text: ' '.repeat(indent) + t,
        bold,
      })),
    );
  const rule = (char = '-') => lines.push({ text: char.repeat(width) });
  const gap = () => lines.push({ text: '' });
  const heading = (text: string) => {
    gap();
    lines.push({ text: thermalText(text).toUpperCase(), bold: true });
    rule();
  };
  /** Double width and height, centred — half the columns, so the writer wraps it, not the printer. */
  const big = (text: string) => {
    for (const t of wrap(thermalText(text), half)) {
      lines.push({ text: centre(t, half), bold: true, double: true });
    }
  };

  return { lines, plain, centred, row, rule, gap, heading, big };
}

/* ------------------------------------------------------------ the document */

export function receiptLines(input: ReceiptInput): ReceiptLine[] {
  const { booking, letterhead, payment, paper } = input;
  const width = paperColumns[paper];
  const symbol = thermalText(letterhead.currencySymbol);
  const money = (amount: number) => `${symbol}${formatMoney(amount)}`;
  const { lines, plain, centred, row, rule, gap, heading, big } = thermalWriter(width);

  /* The shop. */
  big(letterhead.tenantName);

  centred(letterhead.branchName);
  letterhead.addressLines.forEach((line) => centred(line));

  if (letterhead.phone) {
    centred(`Ph: ${letterhead.phone}`);
  }

  if (letterhead.gstin) {
    centred(`GSTIN: ${letterhead.gstin}`);
  }

  rule('=');

  /* What this paper is. */
  if (payment) {
    const refund = payment.amount < 0;
    const all = [payment, ...(input.together ?? [])];

    centred(refund ? 'REFUND RECEIPT' : 'PAYMENT RECEIPT', true);
    gap();
    all.forEach((taken) => row('Receipt', taken.receiptNumber));
    row('Date', instant(payment.receivedOn));
    row('Booking', referenceLabel(booking));
    row('Customer', booking.customerName);
    row('Mobile', booking.customerMobile);
    gap();

    // The figure the customer is handed paper for, at double size: on a crowded counter this is
    // the one number anyone checks. Taken together, it is what they handed over in all.
    const figure = money(Math.abs(all.reduce((sum, taken) => sum + taken.amount, 0)));
    lines.push({ text: refund ? 'Paid back' : 'Received', bold: true });

    big(figure);

    if (all.length === 1) {
      row('For', paymentKindLabel[payment.kind]);
    } else {
      all.forEach((taken) => row(paymentKindLabel[taken.kind], money(Math.abs(taken.amount))));
    }

    row('By', paymentMethodLabel[payment.method]);

    if (payment.reference) {
      row('Ref', payment.reference);
    }
  } else {
    centred('BOOKING SUMMARY', true);
    gap();
    row('Booking', referenceLabel(booking));
    row('Customer', booking.customerName);
    row('Mobile', booking.customerMobile);
  }

  /* The charges — what the rental costs, and what is still to pay against it. */
  const charges = chargeBlock(booking);
  const phase = moneyPhase(booking);

  heading('Charges');

  for (const line of charges.lines) {
    row(line.label, `${line.direction === 'less' ? '-' : ''}${money(line.amount)}`);
  }

  rule();
  row('Invoice total', money(charges.invoiceTotal), true);

  for (const receipt of charges.receipts) {
    // Rent given back adds to what is owed rather than settling it — a plus on the roll too.
    row(receipt.label, `${receipt.givenBack ? '+' : '-'}${money(receipt.amount)}`);
  }

  if (charges.balance > 0) {
    row(phase === 'beforePickup' ? 'Balance to pay' : 'Still to pay', money(charges.balance), true);
  } else {
    row('Balance', 'Paid', true);
  }

  if (charges.alterationsAbsorbed) {
    plain('Some alteration was done free of charge.');
  }

  /* The deposit — its own block with its own subtotal, never inside the invoice total. */
  const settled = settledDeposit(booking);

  if (settled) {
    heading('Deposit settled');
    row('Held', money(settled.held));

    for (const deduction of settled.named) {
      row(
        deduction.wearerName ? `${deduction.label} - ${deduction.wearerName}` : deduction.label,
        `-${money(deduction.amount)}`,
      );
    }

    if (settled.unnamed > 0) {
      row('Late fee and other charges', `-${money(settled.unnamed)}`);
    }

    if (settled.towardsRent > 0) {
      row('Towards the rent owed', `-${money(settled.towardsRent)}`);
    }

    row('Refunded to you', money(settled.refunded), true);
  } else {
    const deposit = depositBlock(booking);

    if (deposit.onHold > 0) {
      heading('Security deposit');

      if (deposit.due > 0) {
        row('Deposit to pay', money(deposit.due));
      }

      if (deposit.held > 0) {
        row(deposit.due > 0 ? 'Already with us' : 'Held', money(deposit.held));
      }

      row('Refundable', money(deposit.onHold), true);
      plain('Not part of the invoice. Returned after the garments come back, less any charges.');
    }
  }

  /* The terms — before the return only. */
  if (phase !== 'settled') {
    const exposure = returnExposure(booking);

    heading('Return by');
    lines.push({ text: thermalText(formatDocumentDate(booking.possessionToDate)), bold: true });
    gap();

    // Null is not zero: a shop with no rate has not decided a late day is free.
    if (exposure.lateFeePerDay !== null) {
      const grace =
        exposure.graceDays > 0
          ? ` after ${exposure.graceDays} day${exposure.graceDays === 1 ? '' : 's'} grace`
          : '';
      row(`Late${grace}`, `${money(exposure.lateFeePerDay)}/day`);
    }

    for (const accessory of exposure.accessories) {
      row(
        `${accessory.name}${accessory.quantity > 1 ? ` x${accessory.quantity}` : ''} not returned`,
        money(accessory.total),
      );
    }

    // Never a figure: damage is assessed against photographs at the return counter.
    row('Damage', 'at inspection');
    plain(
      exposure.unsecured
        ? 'No deposit is held, so anything charged is payable separately.'
        : 'Deducted from the deposit. Anything above it is payable separately.',
    );
  }

  /* The foot. */
  rule('=');

  // The booking's number as a QR, so the phone's Scan tab or a counter scanner opens the booking
  // straight off the paper — the website's customer copy carries the same. An enquiry has no
  // number, and a code that scans as nothing is worse than none: the clerk tries it first.
  const reference = bookingReference(booking);

  if (reference.kind !== 'unnumbered' && fits(reference.number)) {
    lines.push({ text: '', qr: encodeQr(reference.number) });
    centred(reference.number);
    gap();
  }

  if (payment?.receivedByName) {
    centred(`Received by ${payment.receivedByName}`);
  }

  centred('Thank you');
  centred(`Printed ${instant(input.printedAt)}`);

  return lines;
}

/** An instant, in the device's own time, spelt out for paper that leaves the shop. */
export function instant(value: string): string {
  const parsed = dayjs(value);

  return parsed.isValid() ? parsed.format('D MMM YYYY, h:mm A') : '-';
}
